/* @flow */
import * as React from 'react';
import { withTranslation } from 'react-i18next';
import { uniq, isEqual } from 'lodash';
import LicenseComponentListHeader from '../../../src/components/LicenseComponentListHeader';
import LicenseComponentListRow from '../../../src/components/LicenseComponentListRow';
import ActionButtons from 'components/ActionButtons';
import protonesUtils from '../../../utils/protones';

import type { MergeLicenseComponent, Translate } from 'types';
import styles from './styles.module.scss';

type Props = {
  components?: MergeLicenseComponent[],
  selectedComponents?: string[],
  onComponentCheckboxClick?: (componentId: string) => mixed,
  onComponentHeaderCheckboxClick?: (componentId: string) => mixed,
  onAskQuotation?: (components: MergeLicenseComponent[]) => mixed,
  onBuyComponents?: (components: MergeLicenseComponent[]) => mixed,
  t: Translate,
};

type State = {
  selectedRows: string[],
};

class TrackListRowAccordionLicenseComponents extends React.PureComponent<Props, State> {
  state = {
    selectedRows: this.props.selectedComponents || [],
  };

  componentDidMount() {
    // this._selectAllRows();
  }

  componentDidUpdate(prevProps: Props) {
    const { selectedComponents } = this.props;
    if (!isEqual(selectedComponents, prevProps.selectedComponents)) {
      this.setState({ selectedRows: selectedComponents });
    }
  }

  _handleAskQuotation = (components: MergeLicenseComponent[]) => {
    const { onAskQuotation } = this.props;
    if (onAskQuotation != null) {
      onAskQuotation(components);
    }
  };

  _handleBuyComponents = (components: MergeLicenseComponent[]) => {
    const { onBuyComponents } = this.props;
    if (onBuyComponents != null) {
      onBuyComponents(components);
    }
  };

  _selectRows = (rowIds: string[]) => {
    const { selectedRows } = this.state;
    this.setState({ selectedRows: uniq([...selectedRows, ...rowIds]) });
  };

  _selectAllRows = () => {
    const { components = [] } = this.props;
    const allRowsIds = components.map(component => component.id);
    this._selectRows(allRowsIds);
  };

  _deselectRows = (rowIds: string[]) => {
    const { selectedRows } = this.state;
    this.setState({ selectedRows: selectedRows.filter(rowId => !rowIds.includes(rowId)) });
  };

  _deselectAllRows = () => {
    const { components = [] } = this.props;
    const allRowsIds = components.map(component => component.id);
    this._deselectRows(allRowsIds);
  };

  _handleToggleRow = (rowId: string) => {
    const { onComponentCheckboxClick } = this.props;
    if (onComponentCheckboxClick) {
      return onComponentCheckboxClick(rowId);
    }
    const { selectedRows } = this.state;
    const nextSelectedRows = selectedRows.includes(rowId)
      ? selectedRows.filter(id => id !== rowId)
      : [...selectedRows, rowId];
    this.setState({ selectedRows: nextSelectedRows });
  };

  _getIsRowSelected = (rowId: string) => {
    const { selectedRows } = this.state;
    return selectedRows.includes(rowId);
  };

  _getIsAllRowsSelected = () => {
    const { components = [] } = this.props;
    const allRowsIds = components.map(component => component.id);
    const isAllRowsSelected = allRowsIds.every(this._getIsRowSelected);
    return isAllRowsSelected;
  };

  _handleToggleAllRows = () => {
    const { onComponentHeaderCheckboxClick } = this.props;
    if (onComponentHeaderCheckboxClick) {
      return onComponentHeaderCheckboxClick();
    }
    const isAllRowsSelected = this._getIsAllRowsSelected();
    if (isAllRowsSelected) {
      this._deselectAllRows();
    } else {
      this._selectAllRows();
    }
  };

  _getActionButtonsForHeader = () => {
    const actionButtons = {};
    const { components = [], onAskQuotation, onBuyComponents } = this.props;
    const { selectedRows } = this.state;
    const selectedComponents = components.filter(component => selectedRows.includes(component.id));
    if (selectedComponents.length === 0) {
      return {};
    }
    const unpricedComponents = selectedComponents.filter(
      protonesUtils.getCanAskQuotationForLicenseComponent
    );
    const pricedComponents = selectedComponents.filter(protonesUtils.getCanBuyLicenseComponent);
    if (onAskQuotation && unpricedComponents.length > 0) {
      actionButtons.ask = { data: this._handleAskQuotation.bind(this, unpricedComponents) };
    }
    if (onBuyComponents && pricedComponents.length > 0) {
      if (pricedComponents.every(component => component.components.every(c => c.buyable))) {
        actionButtons.buy = {
          data: [],
          onClick: this._handleBuyComponents.bind(this, pricedComponents),
        };
      } else {
        actionButtons.cannotBuy = { data: () => {} };
      }
    }
    return actionButtons;
  };

  _getActionButtonsForRow = (component: MergeLicenseComponent) => {
    const { onAskQuotation, onBuyComponents } = this.props;
    const actionButtons = {};
    if (onBuyComponents && protonesUtils.getCanBuyLicenseComponent(component)) {
      if (component.components.every(c => c.buyable)) {
        actionButtons.buy = {
          data: [],
          onClick: this._handleBuyComponents.bind(this, [component]),
        };
      } else {
        actionButtons.cannotBuy = { data: () => {} };
      }
    } else {
      if (onAskQuotation && protonesUtils.getCanAskQuotationForLicenseComponent(component)) {
        actionButtons.ask = { data: this._handleAskQuotation.bind(this, [component]) };
      }
    }
    return actionButtons;
  };

  render() {
    const { components = [], t } = this.props;
    const { selectedRows } = this.state;
    const hasComponents = components != null && components.length > 0;
    const isAllRowsSelected = components
      .map(component => component.id)
      .every(id => selectedRows.includes(id));
    return hasComponents ? (
      <div className={styles.container}>
        <LicenseComponentListHeader
          isSelected={isAllRowsSelected}
          onCheckboxClick={this._handleToggleAllRows}
          actionButtons={<ActionButtons {...this._getActionButtonsForHeader()} />}
        />
        {components.map((component, index) => (
          <LicenseComponentListRow
            key={`${component.id}`}
            isSelected={selectedRows.includes(component.id)}
            component={component}
            onCheckboxClick={this._handleToggleRow.bind(this, component.id)}
            actionButtons={<ActionButtons {...this._getActionButtonsForRow(component)} />}
          />
        ))}
      </div>
    ) : (
      <div className={styles.container}>
        <p>{t('NO_COMPONENTS')}</p>
      </div>
    );
  }
}

export default withTranslation()(TrackListRowAccordionLicenseComponents);
